<template>
  <div class="row">
    <div class="col-xl-12">
      <div class="kt-portlet kt-portlet--mobile">
        <!-- Header -->
        <div class="kt-portlet__head kt-portlet__head--lg">
          <div class="kt-portlet__head-label">
            <span class="kt-portlet__head-icon">
              <i class="fas fa-chart-bar"></i>
            </span>
            <h3 class="kt-portlet__head-title">
              Stock Cover Days — Report Analysis
            </h3>
          </div>
          <div class="kt-portlet__head-toolbar">
            <button
              type="button"
              class="btn btn-outline-primary btn-sm"
              @click="exportExcel"
              :disabled="isExporting"
            >
              <i :class="isExporting ? 'fas fa-spinner fa-spin' : 'fas fa-file-excel'"></i>
              {{ isExporting ? 'กำลังดาวน์โหลด...' : 'Export Excel' }}
            </button>
          </div>
        </div>

        <div class="kt-portlet__body">
          <!-- ── FILTER BAR (§3) ─────────────────────────────────────── -->
          <form class="kt-form" @submit.prevent="loadReport">
            <!-- แถว 1: คลังสินค้า · ชื่อสินค้า · แบรนด์สินค้า (#34/#40) -->
            <div class="row">
              <div class="col-xl-4 col-lg-4 col-md-6">
                <div class="form-group">
                  <label>คลังสินค้า:</label>
                  <select2
                    :css-style="'width: 100%;'"
                    :css-class="'form-control m-select2'"
                    :id="'raPlantCodes'"
                    :name="'raPlantCodes'"
                    :select2-options="plantOptions"
                    v-model="filterPlantCodes"
                    ref="raPlantCodesSelect2"
                    multiple
                  ></select2>
                </div>
              </div>

              <div class="col-xl-4 col-lg-4 col-md-6">
                <div class="form-group">
                  <label>ชื่อสินค้า:</label>
                  <select2
                    :css-style="'width: 100%;'"
                    :css-class="'form-control m-select2'"
                    :id="'raProductCodes'"
                    :name="'raProductCodes'"
                    :select2-options="productOptions"
                    v-model="filterProductCodes"
                    ref="raProductCodesSelect2"
                    multiple
                  ></select2>
                </div>
              </div>

              <div class="col-xl-4 col-lg-4 col-md-6">
                <div class="form-group">
                  <label>แบรนด์สินค้า:</label>
                  <!-- #72: control แบรนด์ — wire เข้า Search (BrandIds) แล้ว; BE resolve แบรนด์→รหัสสินค้า→กรอง (เหมือน Dashboard) -->
                  <select2
                    :css-style="'width: 100%;'"
                    :css-class="'form-control m-select2'"
                    :id="'raBrandIds'"
                    :name="'raBrandIds'"
                    :select2-options="brandOptions"
                    v-model="filterBrandIds"
                    ref="raBrandIdsSelect2"
                    multiple
                  ></select2>
                </div>
              </div>
            </div>

            <!-- แถว 2: ปี · Version ข้อมูล · หน่วย -->
            <div class="row">
              <div class="col-xl-4 col-lg-4 col-md-6">
                <div class="form-group">
                  <label class="label-required">ปี:</label>
                  <select class="form-control" v-model.number="filterYear">
                    <option v-for="y in yearOptions" :key="y" :value="y">{{ y }}</option>
                  </select>
                </div>
              </div>

              <div class="col-xl-4 col-lg-4 col-md-6">
                <div class="form-group">
                  <label>Version ข้อมูล:</label>
                  <select class="form-control" v-model="filterVersion">
                    <option value="">ล่าสุด</option>
                    <option v-for="v in versionOptions" :key="v" :value="String(v)">Version{{ v }}</option>
                  </select>
                </div>
              </div>

              <div class="col-xl-4 col-lg-4 col-md-6">
                <div class="form-group">
                  <label class="label-required">หน่วย:</label>
                  <select class="form-control" v-model="filterUnit">
                    <option value="ลิตร">ลิตร</option>
                    <option value="ถัง">ถัง</option>
                  </select>
                </div>
              </div>
            </div>

            <div class="row mb-3">
              <div class="col-12">
                <button type="submit" class="btn btn-primary">
                  <i class="fas fa-search"></i> แสดงข้อมูล
                </button>
                <button type="button" class="btn btn-secondary" @click="resetFilter">
                  ล้างค่าทั้งหมด
                </button>
              </div>
            </div>
          </form>

          <hr />

          <!-- Loading -->
          <div v-if="isLoading" class="text-center py-5">
            <div class="kt-spinner kt-spinner--lg kt-spinner--brand"></div>
            <div class="mt-3">กำลังโหลดข้อมูล...</div>
          </div>

          <div v-else-if="!report" class="text-center text-muted py-4">
            กดปุ่ม "แสดงข้อมูล" เพื่อดูรายงาน
          </div>

          <div v-else>
            <!-- ── RESULT REPORT HEADER (§4) — 2 columns ────────────── -->
            <!-- #42: ใช้ flex ธรรมดา (ไม่ใช่ .row) เพื่อไม่ให้ negative margin ทำให้ header เลยขอบตาราง -->
            <div class="mb-3 scd-summary">
              <div class="scd-summary-col">
                <div><span class="font-weight-bold">คลังสินค้า :</span> {{ plantSummary }}</div>
                <div><span class="font-weight-bold">ชื่อสินค้า :</span> {{ productSummary }}</div>
                <!-- TODO: bind brand from BE (issue #37) -->
                <div><span class="font-weight-bold">แบรนด์สินค้า :</span> {{ brandSummary }}</div>
                <div><span class="font-weight-bold">ปี :</span> {{ report.year }}</div>
                <div><span class="font-weight-bold">Version ข้อมูล :</span> {{ versionLabel }}</div>
              </div>
              <div class="scd-summary-col d-flex flex-column">
                <div><span class="font-weight-bold">Current Date :</span> {{ currentDateDisplay }}</div>
                <div><span class="font-weight-bold">Day of week :</span> {{ dayOfWeekDisplay }}</div>
                <div class="text-right mt-auto"><span class="font-weight-bold">หน่วย :</span> {{ report.unit }}</div>
              </div>
            </div>

            <!-- ── ① ข้อมูลตามเป้าหมาย (Target) — aggregate (ทุก SKU ที่เลือก) · #41: ตัด h5 heading, ใช้ชื่อในแถวหัวเหลืองแทน ── -->

            <div v-if="report.targetRows.length === 0" class="text-center text-muted py-3">
              ไม่พบข้อมูลตามเป้าหมายตามเงื่อนไขที่เลือก
            </div>

            <div v-else class="table-responsive mb-4">
              <table class="table table-bordered table-sm scd-matrix mb-0">
                <colgroup>
                  <col class="scd-col-label" />
                  <col v-for="(lbl, i) in monthLabels" :key="'tcol-' + i" class="scd-col-month" />
                  <col class="scd-col-total" />
                </colgroup>
                <tbody>
                  <!-- Row 1: จำนวนวันขาย (shared) + Total header -->
                  <tr>
                    <th class="scd-label scd-row-saledays">จำนวนวันขาย</th>
                    <td v-for="(sd, i) in report.saleDaysByMonth" :key="'t-sd-' + i"
                      class="scd-row-saledays text-center">{{ sd }}</td>
                    <th class="scd-col-total-cell text-center">Total</th>
                  </tr>
                  <!-- Row 2: month header (yellow) — LEFT cell = table title -->
                  <tr>
                    <th class="scd-label scd-row-months">{{ targetTitle }}</th>
                    <td v-for="(lbl, i) in monthLabels" :key="'t-ml-' + i"
                      class="scd-row-months text-center font-weight-bold">{{ lbl }}</td>
                    <th class="scd-col-total-cell"></th>
                  </tr>
                  <!-- Measure rows (BG Stock / Demand / Supply / SCVD) + Total column -->
                  <tr v-for="mrow in measureRows" :key="'t-' + mrow.key">
                    <th class="scd-label" :class="mrow.cls">{{ mrow.label }}</th>
                    <td v-for="cell in totalMonths(report.targetTotal)" :key="'t-c-' + mrow.key + '-' + cell.month"
                      :class="mrow.cls" class="text-center">
                      <span v-if="!cell.hasData"></span>
                      <span v-else-if="mrow.key === 'scvd'">{{ formatScvd(cell.scvd) }}</span>
                      <span v-else>{{ formatNum(cell[mrow.key]) }}</span>
                    </td>
                    <td class="scd-col-total-cell text-center">
                      <span v-if="mrow.key === 'demand' || mrow.key === 'supply'">{{ formatNum(measureTotal(report.targetTotal, mrow.key)) }}</span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <!-- ── ② ข้อมูลตามหน้างานจริง (Actual) — aggregate (ทุก SKU ที่เลือก) · #41: ตัด h5 heading ── -->

            <div v-if="report.actualBlocked || report.actualRows.length === 0"
              class="text-center text-muted py-3">
              ไม่พบข้อมูลตามหน้างานจริงตามเงื่อนไขที่เลือก
            </div>

            <div v-else class="table-responsive">
              <table class="table table-bordered table-sm scd-matrix mb-0">
                <colgroup>
                  <col class="scd-col-label" />
                  <col v-for="(lbl, i) in monthLabels" :key="'acol-' + i" class="scd-col-month" />
                  <col class="scd-col-total" />
                </colgroup>
                <tbody>
                  <!-- Row 1: month header (yellow) — LEFT cell = table title, empty Total header (no จำนวนวันขาย row) -->
                  <tr>
                    <th class="scd-label scd-row-months">ข้อมูลตามหน้างานจริง</th>
                    <td v-for="(lbl, i) in monthLabels" :key="'a-ml-' + i"
                      class="scd-row-months text-center font-weight-bold">{{ lbl }}</td>
                    <th class="scd-col-total-cell"></th>
                  </tr>
                  <!-- Measure rows (BG Stock / Demand / Supply / SCVD) + Total column -->
                  <tr v-for="mrow in measureRows" :key="'a-' + mrow.key">
                    <th class="scd-label" :class="mrow.cls">{{ mrow.label }}</th>
                    <td v-for="cell in totalMonths(report.actualTotal)" :key="'a-c-' + mrow.key + '-' + cell.month"
                      :class="mrow.cls" class="text-center">
                      <span v-if="!cell.hasData"></span>
                      <span v-else-if="mrow.key === 'scvd'">{{ formatScvd(cell.scvd) }}</span>
                      <span v-else>{{ formatNum(cell[mrow.key]) }}</span>
                    </td>
                    <td class="scd-col-total-cell text-center">
                      <span v-if="mrow.key === 'demand' || mrow.key === 'supply'">{{ formatNum(measureTotal(report.actualTotal, mrow.key)) }}</span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import select2 from "../../Shared/Select2";

require("sweetalert2");

export default {
  components: {
    select2,
  },
  data: function () {
    var self = this;
    var currentYear = new Date().getFullYear();
    var authTransport = function (params, success, failure) {
      params.beforeSend = function (request) {
        request.setRequestHeader(
          "Authorization",
          "Bearer " + self.$root.user.access_token
        );
      };
      var $request = $.ajax(params);
      $request.then(success);
      $request.fail(failure);
      return $request;
    };

    return {
      isLoading: false,
      isExporting: false,
      report: null,

      // ── Filter state ──────────────────────────────────────────────
      filterPlantCodes: [],
      filterProductCodes: [],
      filterBrandIds: [], // #72: brand filter — wired เข้า Search (BrandIds) → BE กรองสินค้าตามแบรนด์ (เหมือน Dashboard)
      filterUnit: "ลิตร",
      filterVersion: "", // "" = ล่าสุด (per-SKU)
      filterYear: currentYear,

      // ปี: ย้อนหลัง 5 ปี (รวมปีปัจจุบัน) + ล่วงหน้า 1 ปี (§3)
      yearOptions: [
        currentYear + 1,
        currentYear,
        currentYear - 1,
        currentYear - 2,
        currentYear - 3,
        currentYear - 4,
      ],
      // Version dropdown — โหลดรายการ version จริงของ key (plant/product/year) จาก endpoint ReportAnalysis/Versions (issue #33)
      versionOptions: [],

      monthLabels: ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"],
      measureRows: [
        { key: "bgStock", label: "BG Stock", cls: "scd-row-bg" },
        { key: "demand", label: "Demand", cls: "scd-row-demand" },
        { key: "supply", label: "Supply", cls: "scd-row-supply" },
        { key: "scvd", label: "SCVD", cls: "scd-row-scvd" },
      ],

      // Plant Select2 — reuse the same scoped endpoint the Master pages use (F6)
      plantOptions: {
        placeholder: "เลือกคลังสินค้า",
        allowClear: true,
        ajax: {
          url: "/Api/StockCoverDayTarget/Select2Plant",
          type: "post",
          delay: 300,
          dataType: "json",
          contentType: "application/json",
          data: function (params) {
            return JSON.stringify({
              plantId: [],
              search: params.term || "",
              page: params.page || 1,
            });
          },
          processResults: function (data) {
            return { results: data.results, pagination: { more: data.more } };
          },
          transport: authTransport,
        },
        language: { noResults: function () { return "ไม่พบข้อมูล Plant"; } },
      },

      // Product Select2 — reuse the same endpoint (สินค้าสำเร็จรูป น้ำเบียร์) as the Master pages
      productOptions: {
        placeholder: "เลือกสินค้า",
        allowClear: true,
        ajax: {
          url: "/Api/Product/Select2Product",
          delay: 300,
          data: function (params) {
            return {
              productGroupId: 14,
              productTypeId: 22,
              onlyStockCoverDays: true, // CR-StockCoverDays A3: เฉพาะ SKU ที่เปิดใช้งาน SCVD
              onlyHasScvdMaster: true, // CR-StockCoverDays C3: แสดงเฉพาะสินค้าที่มีข้อมูล Master (StockCoverDay_Target)
              search: params.term || "",
              page: params.page || 1,
            };
          },
          processResults: function (data) {
            return { results: data.results, pagination: { more: data.more } };
          },
          transport: authTransport,
        },
        language: { noResults: function () { return "ไม่พบข้อมูลสินค้า"; } },
      },

      // Brand Select2 — reuse ProductBrand endpoint (เหมือน Dashboard) · #40: layout control เท่านั้น ยังไม่ wire เข้า filter (Open Question)
      brandOptions: {
        placeholder: "เลือกแบรนด์สินค้า",
        allowClear: true,
        ajax: {
          url: "/Api/ProductBrand/Select2ProductBrand",
          delay: 300,
          data: function (params) {
            return { search: params.term || "", page: params.page || 1 };
          },
          processResults: function (data) {
            return { results: data.results, pagination: { more: data.more } };
          },
          transport: authTransport,
        },
        language: { noResults: function () { return "ไม่พบข้อมูลแบรนด์"; } },
      },
    };
  },
  computed: {
    plantSummary: function () {
      if (!this.report || !this.report.plants || this.report.plants.length === 0) return "ทั้งหมด";
      return this.report.plants
        .map(function (p) { return "[" + p.plantCode + "] " + p.plantName; })
        .join(", ");
    },
    productSummary: function () {
      if (!this.report || !this.report.products || this.report.products.length === 0) return "ทั้งหมด";
      return this.report.products
        .map(function (p) { return p.productName + " [" + p.productCode + "]"; })
        .join(", ");
    },
    // แบรนด์สินค้า (display-only) — TODO: bind brand from BE (issue #37)
    brandSummary: function () {
      if (!this.report || !this.report.brands || this.report.brands.length === 0) return "ทั้งหมด";
      return this.report.brands
        .map(function (b) {
          if (b === null || b === undefined) return "";
          return b.brandName || b.name || b.brand || String(b);
        })
        .filter(function (n) { return n !== ""; })
        .join(", ");
    },
    // ชื่อตาราง ① (แถวหัวเดือน) — รวม Version ถ้ามี
    // #41: แสดงชื่อ + Version ในแถวหัวเหลือง เสมอ (ใช้ resolvedVersions เมื่อเลือก "ล่าสุด")
    targetTitle: function () {
      var base = "ข้อมูลตามเป้าหมาย";
      var r = this.report;
      if (!r) return base;
      if (r.version) return base + "(Version" + r.version + ")";
      var rv = r.resolvedVersions || [];
      if (rv.length === 1) return base + "(Version" + rv[0] + ")";
      if (rv.length > 1) return base + "(Version" + rv.join(",") + ")";
      return base;
    },
    // Current Date — ใช้ค่าจาก BE ถ้ามี มิฉะนั้นคำนวณฝั่ง client (วว/ดด/ปปปป)
    currentDateDisplay: function () {
      if (this.report && this.report.currentDate) return this.report.currentDate;
      return this.formatCurrentDate(new Date());
    },
    // Day of week (ภาษาไทย) — ใช้ค่าจาก BE ถ้ามี มิฉะนั้นคำนวณฝั่ง client
    dayOfWeekDisplay: function () {
      if (this.report && this.report.dayOfWeek) return this.report.dayOfWeek;
      return this.thaiDayName(new Date());
    },
    // Version ข้อมูล (header ผลลัพธ์) — สะท้อน version จริงของรายงานที่โหลดมา (issue #38)
    //  • เลือก version เฉพาะ (report.version มีค่า)                → "Version{N}"
    //  • เลือก "ล่าสุด" → ใช้ report.resolvedVersions จาก Search:
    //      - 1 ค่า  → "Version{v} (ล่าสุด)"
    //      - >1 ค่า → "Version{v1},{v2},… (ล่าสุด)"
    //      - ว่าง/ไม่มี → "ล่าสุด"
    versionLabel: function () {
      if (!this.report) return "";
      if (this.report.version) {
        return "Version" + this.report.version;
      }
      var resolved = this.report.resolvedVersions;
      if (resolved && resolved.length === 1) {
        return "Version" + resolved[0] + " (ล่าสุด)";
      }
      if (resolved && resolved.length > 1) {
        return "Version" + resolved.join(",") + " (ล่าสุด)";
      }
      return "ล่าสุด";
    },
  },
  methods: {
    buildPayload: function () {
      return {
        PlantCodes: this.filterPlantCodes || [],
        ProductCodes: this.filterProductCodes || [],
        // #72: แบรนด์สินค้า → BrandIds (int[]) — BE resolve เป็นรหัสสินค้าแล้วกรอง (เหมือน Dashboard)
        BrandIds: (this.filterBrandIds || []).map(Number),
        Unit: this.filterUnit,
        Version: this.filterVersion === "" ? null : parseInt(this.filterVersion, 10),
        Year: parseInt(this.filterYear, 10),
      };
    },

    // โหลดรายการ Version จริงของ key ที่เลือก (plant/product/year) — issue #33
    // mirror การเรียก Search: base path/pattern + { success, data } envelope เดียวกัน
    loadVersions: function (override) {
      var self = this;
      override = override || {};
      var reqYear = override.year !== undefined ? override.year : parseInt(self.filterYear, 10);
      var reqPlants = override.plantCodes !== undefined ? override.plantCodes : (self.filterPlantCodes || []);
      var reqProducts = override.productCodes !== undefined ? override.productCodes : (self.filterProductCodes || []);
      axios
        .post(
          "/Api/StockCoverDayTarget/ReportAnalysis/Versions",
          {
            Year: reqYear,
            PlantCodes: reqPlants,
            ProductCodes: reqProducts,
          },
          { headers: { Authorization: "Bearer " + self.$root.user.access_token } }
        )
        .then(function (response) {
          var versions = [];
          if (response.data && response.data.success && response.data.data) {
            versions = response.data.data.versions || [];
          }
          self.versionOptions = versions;
          // ถ้า version ที่เลือกอยู่ไม่มีในรายการใหม่ → รีเซ็ตเป็น "ล่าสุด"
          if (self.filterVersion !== "" && versions.indexOf(parseInt(self.filterVersion, 10)) === -1) {
            self.filterVersion = "";
          }
        })
        .catch(function (error) {
          // ไม่บล็อกหน้า — fallback เหลือแค่ตัวเลือก "ล่าสุด"
          console.error("โหลดรายการ Version ไม่สำเร็จ:", error);
          self.versionOptions = [];
          self.filterVersion = "";
        });
    },

    loadReport: function () {
      var self = this;
      self.isLoading = true;
      self.report = null;
      axios
        .post("/Api/StockCoverDayTarget/ReportAnalysis/Search", self.buildPayload(), {
          headers: { Authorization: "Bearer " + self.$root.user.access_token },
        })
        .then(function (response) {
          if (response.data && response.data.success && response.data.data) {
            var d = response.data.data;
            // defensive defaults
            d.saleDaysByMonth = d.saleDaysByMonth || [];
            d.targetRows = d.targetRows || [];
            d.actualRows = d.actualRows || [];
            d.targetTotal = d.targetTotal || { blocked: true, months: [] };
            d.actualTotal = d.actualTotal || { blocked: true, months: [] };
            d.plants = d.plants || [];
            d.products = d.products || [];
            self.report = d;
          } else {
            Swal.fire("เกิดข้อผิดพลาด", (response.data && response.data.message) || "ไม่สามารถโหลดข้อมูลได้", "error");
          }
        })
        .catch(function (error) {
          var message = "เกิดข้อผิดพลาดในการโหลดข้อมูล";
          if (error.response && error.response.data && error.response.data.message) {
            message = error.response.data.message;
          }
          Swal.fire("เกิดข้อผิดพลาด", message, "error");
        })
        .then(function () {
          self.isLoading = false;
        });
    },

    resetFilter: function () {
      var self = this;
      var currentYear = new Date().getFullYear();
      this.filterPlantCodes = [];
      this.filterProductCodes = [];
      this.filterBrandIds = [];
      this.filterUnit = "ลิตร";
      this.filterVersion = "";
      this.filterYear = currentYear;
      $("#raPlantCodes").val(null).trigger("change");
      $("#raProductCodes").val(null).trigger("change");
      $("#raBrandIds").val(null).trigger("change");
      this.report = null;
      // #33: บังคับโหลด Version list กลับเป็นค่าเต็มของสถานะที่ล้างแล้ว
      // (ไม่พึ่ง watcher — กัน timing ของ <select2> ที่ value-watcher sync กลับ widget ถูก comment ไว้)
      this.$nextTick(function () {
        self.loadVersions({ plantCodes: [], productCodes: [], year: currentYear });
      });
    },

    exportExcel: function () {
      var self = this;
      self.isExporting = true;
      KTApp.blockPage({ opacity: 0.05, message: "กำลังสร้างไฟล์ กรุณารอสักครู่..." });
      axios({
        method: "post",
        url: "/Api/StockCoverDayTarget/ReportAnalysis/Export",
        responseType: "arraybuffer",
        data: self.buildPayload(),
        headers: { Authorization: "Bearer " + self.$root.user.access_token },
      })
        .then(function (response) {
          if (response.status === 200) {
            var contentType = response.headers["content-type"];
            var disposition = response.headers["content-disposition"];
            var filename = "StockCoverDays_ReportAnalysis.xlsx";
            if (disposition && disposition.indexOf("attachment") !== -1) {
              var m = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(disposition);
              if (m != null && m[1]) filename = decodeURIComponent(m[1].replace(/['"]/g, ""));
            }
            var blob = new Blob([response.data], { type: contentType });
            var link = document.createElement("a");
            link.href = window.URL.createObjectURL(blob);
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
          } else if (response.status === 204) {
            Swal.fire("ไม่พบข้อมูล", "ไม่พบข้อมูลตามเงื่อนไขที่เลือก", "warning");
          }
        })
        .catch(function () {
          Swal.fire("เกิดข้อผิดพลาด", "ไม่สามารถดาวน์โหลดไฟล์ได้", "error");
        })
        .then(function () {
          self.isExporting = false;
          KTApp.unblockPage();
        });
    },

    // months array ของแถวรวม (ป้องกัน null)
    totalMonths: function (total) {
      return (total && total.months) ? total.months : [];
    },
    // คอลัมน์ Total (เฉพาะ Demand/Supply) = ผลรวม 12 เดือน เฉพาะเดือนที่มีข้อมูล (hasData)
    measureTotal: function (total, key) {
      var months = this.totalMonths(total);
      var sum = 0;
      for (var i = 0; i < months.length; i++) {
        var cell = months[i];
        if (cell && cell.hasData) {
          var v = cell[key];
          if (v !== null && v !== undefined && v !== "") sum += Number(v);
        }
      }
      return sum;
    },
    // BG/Demand/Supply = จำนวนเต็ม (#,##0) ตาม spec §2 · เฉพาะ SCVD = 2 ตำแหน่ง (formatScvd)
    formatNum: function (v) {
      if (v === null || v === undefined || v === "") return "";
      return Number(v).toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 });
    },
    formatScvd: function (v) {
      if (v === null || v === undefined) return "-";
      return Number(v).toFixed(2);
    },
    // วันที่ปัจจุบัน รูปแบบ วว/ดด/ปปปป
    formatCurrentDate: function (d) {
      var dd = ("0" + d.getDate()).slice(-2);
      var mm = ("0" + (d.getMonth() + 1)).slice(-2);
      return dd + "/" + mm + "/" + d.getFullYear();
    },
    // ชื่อวันในสัปดาห์ (ภาษาไทย)
    thaiDayName: function (d) {
      var days = ["วันอาทิตย์", "วันจันทร์", "วันอังคาร", "วันพุธ", "วันพฤหัสบดี", "วันศุกร์", "วันเสาร์"];
      return days[d.getDay()];
    },
  },
  // โหลดรายการ Version ครั้งแรกตามค่า filter เริ่มต้น (issue #33)
  mounted: function () {
    this.loadVersions();
  },
  // โหลดรายการ Version ใหม่เมื่อ plant/product/year ที่เลือกเปลี่ยน (issue #33)
  watch: {
    filterPlantCodes: function () {
      this.loadVersions();
    },
    filterProductCodes: function () {
      this.loadVersions();
    },
    filterYear: function () {
      this.loadVersions();
    },
  },
};
</script>

<style scoped>
.card {
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}
.card-header {
  padding: 0.5rem 1rem;
}
.scd-summary {
  display: flex;
  flex-wrap: wrap;
}
/* #42: 2 คอลัมน์ 50/50 ไม่มี negative margin → ขอบซ้าย/ขวา align กับตารางข้อมูล */
.scd-summary-col {
  flex: 0 0 50%;
  max-width: 50%;
}
@media (max-width: 767px) {
  .scd-summary-col { flex: 0 0 100%; max-width: 100%; }
}
.scd-summary div {
  padding: 2px 0;
}
.scd-matrix {
  min-width: 958px;
  table-layout: fixed;
}
.scd-matrix th,
.scd-matrix td {
  padding: 0.25rem 0.35rem;
  vertical-align: middle;
  white-space: nowrap;
}
/* คอลัมน์ให้ตรงกันทั้ง 2 ตาราง (table-layout: fixed + shared colgroup) */
.scd-col-label { width: 140px; }
.scd-col-month { width: 62px; }
.scd-col-total { width: 74px; }
.scd-label {
  width: 140px;
  text-align: center; /* #41: คอลัมน์แรก (label) กึ่งกลาง */
  font-weight: 600;
}
/* §1.2.1 row colors (reference จาก Excel) — same palette as the Detail matrix */
.scd-row-saledays { background-color: #d9d9d9; }
.scd-row-months { background-color: #ffe599; }
.scd-row-bg { background-color: #cfe2f3; }
.scd-row-demand { background-color: #d9ead3; }
.scd-row-supply { background-color: #ead1dc; }
.scd-row-scvd { background-color: #d9d2e9; }
/* คอลัมน์ Total = เทา (§C) */
.scd-col-total-cell { background-color: #d9d9d9; }
/* แถวรวม "ทั้งหมด" = เน้นกรอบให้ต่างจากราย SKU */
.scd-total-card {
  border: 2px solid #b0bec5;
}
.scd-total-card .card-header {
  background-color: #eceff1 !important;
}
</style>



// WEBPACK FOOTER //
// App/Components/StockCoverDays/StockCoverDaysReportAnalysis.vue